// src/ErrorPages/ErrorBoundary.tsx
import React, { Component, ReactNode } from "react";
import { assetsUrl } from "../constants";

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = {
    hasError: false,
    error: null,
    errorInfo: null,
  };

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("ErrorBoundary caught an error:", error, errorInfo);
    this.setState({ errorInfo });
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  resetError = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };
  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      return <ErrorPage resetError={this.resetError} />;
    }
    return this.props.children;
  }
}

export const ErrorPage = ({resetError}:{resetError?:()=>void})=>{
  const reset = ()=>{
     if(resetError){
      resetError()
     }
  }
  return (
    <div
      className="flex flex-col items-ceter justify-start h-screen bg-cover bg-center"
      style={{
        backgroundImage: `url("${assetsUrl}/errors/application_error/bg6.jpg")`,
      }}
    >
      <div style={errorContainerStyle}>
        <h2 style={headingStyle}>Something went wrong !</h2>
        <p style={messageStyle}>
          An unexpected error occurred. Please try again or contact support.
        </p>
        <button style={buttonStyle} onClick={reset}>
          Retry
        </button>
      </div>
    </div>
  )
}

// Inline styles (move to CSS if preferred)
const errorContainerStyle: React.CSSProperties = {
  padding: "20px",
  textAlign: "center",
  maxWidth: "600px",
  margin: "50px auto",
  border: "1px solid #ff4d4f",
  borderRadius: "8px",
  backgroundColor: "#fff1f0",
};

const headingStyle: React.CSSProperties = {
  color: "#ff4d4f",
  marginBottom: "10px",
  fontSize:"1.6rem"
};
const messageStyle: React.CSSProperties = {
  color: "#333",
  marginBottom: "20px",
};

const buttonStyle: React.CSSProperties = {
  padding: "10px 20px",
  backgroundColor: "#1890ff",
  color: "#fff",
  border: "none",
  borderRadius: "4px",
  cursor: "pointer",
};

export default ErrorBoundary;
